import './ShareReport.scss';
import React from 'react';
import { FormikProps } from 'formik';
import {
  IonCheckbox,
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonRow,
} from '@ionic/react';
import { useIntl } from 'react-intl';
import { JobMessages } from '../../intl/JobMessages';
import { GenericMessages } from '../../intl/GenericMessages';
import { Input } from '../reusable-components/Input';
import { LONG_EXPIRATION_IN_DAYS, MEDIUM_EXPIRATION_IN_DAYS, SHORT_EXPIRATION_IN_DAYS } from '../../models/jobLog';
import { DqText } from '../dq-text';
import { informationCircle } from 'ionicons/icons';

export interface GuestShareFormData {
  firstName: string;
  lastName: string;
  emailAddress: string;
  externalOrgName: string;
  daysToExpiration: string;
  tubes: boolean;
  annotations: boolean;
  timestamps: boolean;
  pressure: boolean;
  pieCharts: boolean;
  idleVsPressure: boolean;
  configurations: boolean;
}

interface ShareReportFormProps {
  /**
   * State, handlers, and helpers made available to form component or render prop of.
   */
  formikProps: FormikProps<GuestShareFormData>;
}

export const GuestShareForm: React.FC<ShareReportFormProps> = ({ formikProps }) => {
  const { formatMessage } = useIntl();

  return (
    <IonGrid className="ion-no-padding">
      <IonRow>
        <IonCol className="guest-share-left" sizeXl="6" sizeLg="6" sizeMd="6" sizeSm="12" sizeXs="12">
          <IonRow>
            <IonLabel>{formatMessage(JobMessages.shareRecipientDetailsLabel)}</IonLabel>
          </IonRow>
          <Input
            name="emailAddress"
            label={formatMessage(GenericMessages.emailAddressLabel)}
            required
            value={formikProps.values.emailAddress}
            onChange={formikProps.handleChange}
            errorText={formikProps.touched.emailAddress ? formikProps.errors.emailAddress : undefined}
            id="email-address-input"
          />
          <IonIcon class="ion-no-margin ion-no-padding note-icon" icon={informationCircle} />
          <DqText class={'ion-margin'}>{formatMessage(JobMessages.mayShareWithAllOrgMembersMessage)}</DqText>
          <IonRow className="top-border" />
          <Input
            name="firstName"
            label={formatMessage(GenericMessages.firstNameColumnLabel)}
            required
            value={formikProps.values.firstName}
            onChange={formikProps.handleChange}
            errorText={formikProps.touched.firstName ? formikProps.errors.firstName : undefined}
            id="first-name-input"
          />
          <Input
            name="lastName"
            label={formatMessage(GenericMessages.lastNameColumnLabel)}
            required
            value={formikProps.values.lastName}
            onChange={formikProps.handleChange}
            errorText={formikProps.touched.lastName ? formikProps.errors.lastName : undefined}
            id="last-name-input"
          />
          <Input
            name="externalOrgName"
            label={formatMessage(GenericMessages.organizationColumnLabel)}
            required
            value={formikProps.values.externalOrgName}
            onChange={formikProps.handleChange}
            errorText={formikProps.touched.externalOrgName ? formikProps.errors.externalOrgName : undefined}
            id="organization-input"
          />
        </IonCol>
        <IonCol className="guest-share-right" sizeXl="6" sizeLg="6" sizeMd="6" sizeSm="12" sizeXs="12">
          <IonRow>
            <IonLabel>{formatMessage(JobMessages.selectInformationToShare)}</IonLabel>
            <IonLabel>{formatMessage(JobMessages.chartsAndGraphs)}</IonLabel>
          </IonRow>
          <IonItem className="tubes-checkbox-item" lines="none">
            <IonCheckbox
              className="tubes-checkbox"
              checked={formikProps.values.tubes}
              onIonChange={() => formikProps.setFieldValue('tubes', !formikProps.values.tubes)}
            />
            <IonLabel className="ion-padding-start">{formatMessage(JobMessages.tubes)}</IonLabel>
          </IonItem>
          <IonItem className="tubes-checkbox-item" lines="none">
            <IonCheckbox
              className="tubes-checkbox"
              checked={formikProps.values.annotations}
              onIonChange={() => formikProps.setFieldValue('annotations', !formikProps.values.annotations)}
            />
            <IonLabel className="ion-padding-start">{formatMessage(JobMessages.annotations)}</IonLabel>
          </IonItem>
          <IonItem className="tubes-checkbox-item" lines="none">
            <IonCheckbox
              className="tubes-checkbox"
              checked={formikProps.values.pressure}
              onIonChange={() => formikProps.setFieldValue('pressure', !formikProps.values.pressure)}
            />
            <IonLabel className="ion-padding-start">{formatMessage(JobMessages.pressure)}</IonLabel>
          </IonItem>
          <IonItem className="tubes-checkbox-item" lines="none">
            <IonCheckbox
              className="tubes-checkbox"
              checked={formikProps.values.pieCharts}
              onIonChange={() => formikProps.setFieldValue('pieCharts', !formikProps.values.pieCharts)}
            />
            <IonLabel className="ion-padding-start">{formatMessage(JobMessages.pieCharts)}</IonLabel>
          </IonItem>
          <IonItem className="tubes-checkbox-item " lines="none">
            <IonCheckbox
              className="tubes-checkbox"
              checked={formikProps.values.idleVsPressure}
              onIonChange={() => formikProps.setFieldValue('idleVsPressure', !formikProps.values.idleVsPressure)}
            />
            <IonLabel className="ion-padding-start">{formatMessage(JobMessages.idleVsPressure)}</IonLabel>
          </IonItem>
          <IonItem className="tubes-checkbox-item" lines="none">
            <IonCheckbox
              className="tubes-checkbox"
              checked={formikProps.values.configurations}
              onIonChange={() => formikProps.setFieldValue('configurations', !formikProps.values.configurations)}
            />
            <IonLabel className="ion-padding-start">{formatMessage(JobMessages.configuration)}</IonLabel>
          </IonItem>
          <IonRow className="ion-padding-vertical">
            <IonLabel>{formatMessage(JobMessages.additionalSettings)}</IonLabel>
          </IonRow>
          <IonItem className="tubes-checkbox-item" lines="none">
            <IonCheckbox
              className="tubes-checkbox"
              checked={formikProps.values.timestamps}
              onIonChange={() => formikProps.setFieldValue('timestamps', !formikProps.values.timestamps)}
            />
            <IonLabel className="ion-padding-start">{formatMessage(JobMessages.timeStamps)}</IonLabel>
          </IonItem>
          <IonRow className="top-border">
            <IonLabel>{formatMessage(JobMessages.shareExpirationLabel)}</IonLabel>
          </IonRow>
          <IonRadioGroup
            name="daysToExpiration"
            value={formikProps.values.daysToExpiration}
            onIonChange={formikProps.handleChange}
          >
            <IonItem className="tubes-checkbox-item" lines="none">
              <IonRadio value={SHORT_EXPIRATION_IN_DAYS.toString()} />
              <IonLabel className="ion-padding-start">{formatMessage(JobMessages.shareExpirationShortLabel)}</IonLabel>
            </IonItem>
            <IonItem className="tubes-checkbox-item" lines="none">
              <IonRadio value={MEDIUM_EXPIRATION_IN_DAYS.toString()} />
              <IonLabel className="ion-padding-start">{formatMessage(JobMessages.shareExpirationMediumLabel)}</IonLabel>
            </IonItem>
            <IonItem className="tubes-checkbox-item" lines="none">
              <IonRadio value={LONG_EXPIRATION_IN_DAYS.toString()} />
              <IonLabel className="ion-padding-start">{formatMessage(JobMessages.shareExpirationLongLabel)}</IonLabel>
            </IonItem>
          </IonRadioGroup>
        </IonCol>
      </IonRow>
    </IonGrid>
  );
};
